import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getErrorMessage,
  hasValidationErrors,
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../common/global/helpers';
import Breadcrumbs from '../../../../../common/widgets/Breadcrumbs';
import Button from '../../../../../common/widgets/Button';
import Checkbox from '../../../../../common/widgets/Checkbox';
import Icons from '../../../../../common/widgets/Icons';
import Input from '../../../../../common/widgets/Input';
import {
  Notification,
  NotificationType,
} from '../../../../../common/widgets/Notification';
import { DemoAccountRequestData } from '../../../models/DemoAccount';
import { ErrorState, ErrorType } from '../../../models/Errors';
import { resources } from '../../../resources/resources';
import { customerService } from '../../../services/customer';
import applicationCrumbs from '../../ApplicationCrumbs';
import PlatformInformation from '../../Common/PlatformInformation';
import MyAccountMenu from '../Common/MyAccountMenu';

interface DemoAccountPageState {
  isSaving: boolean;
  data: DemoAccountRequestData;
}

export default function DemoAccountPage() {
  const { t: translate } = useTranslation();
  const crumbs = applicationCrumbs(translate);

  const [state, setState] = useState({
    isSaving: false,
    data: { companyName: '', demoIncentiveRewards: false },
  } as DemoAccountPageState);
  const [errors, setErrors] = useState<ErrorState>({});

  const handleGenerateAccount = async () => {
    setState(
      produce(d => {
        d.isSaving = true;
      }),
    );
    const response = await customerService.createDemoCustomer(state.data);
    if (!response.hasErrors) {
      showSuccessToast(translate(resources.demoAccount.saveSuccess));
    } else if (response.errorType === ErrorType.ValidationError) {
      setValidationErrors(response, setErrors);
    } else {
      showErrorToast(translate(resources.demoAccount.saveError));
    }
    setState(
      produce(d => {
        d.isSaving = false;
      }),
    );
  };
  return (
    <>
      <Breadcrumbs
        entries={[crumbs.home, crumbs.accountDashboard, crumbs.demoAccount]}
      />
      <section className="section-main section-actions my-account-page change-password-page">
        <div className="container">
          <div className="area-actions">
            <MyAccountMenu />
          </div>
          <div className="area-content">
            <PlatformInformation />
            <div className="panel padding-xl">
              <h1>{translate(resources.demoAccount.title)}</h1>
              {hasValidationErrors(errors, 'null') && (
                <Notification
                  type={NotificationType.Error}
                  messages={errors.null.map(e => ({
                    message: getErrorMessage(e, translate),
                  }))}
                />
              )}
              <div className="upload-step">
                <Input
                  label={translate(resources.demoAccount.textCompany)}
                  value={state.data.companyName}
                  mandatory={true}
                  onChange={e =>
                    setState(
                      produce(d => {
                        d.data.companyName = e.value;
                      }),
                    )
                  }
                  errors={errors.companyName}
                />
              </div>
              <div className="upload-step">
                <Checkbox
                  label={translate(resources.demoAccount.incentiveRewards)}
                  isChecked={state.data.demoIncentiveRewards}
                  onChange={() => {
                    setState(
                      produce(d => {
                        d.data.demoIncentiveRewards =
                          !d.data.demoIncentiveRewards;
                      }),
                    );
                  }}
                />
              </div>
              <div className="form-actions left-actions">
                <Button
                  icon={<Icons.Add />}
                  className="btn btn-solid"
                  onClick={handleGenerateAccount}
                  loading={state.isSaving}
                >
                  <span className="btn-text">
                    {translate(resources.demoAccount.button.generate)}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
